import { nonEmptyString } from '../lib/format';
import { useState } from 'react';
import type { ClioContact, Party, RelatedContact, ProofGraph } from '../types';
import { createContact, type CreateContactPayload } from '../lib/contacts';
import { counselFields, getPartyCounselContactIds, getPartyParalegalContactIds } from '../lib/parties';
import type { ContactFieldsFormValue } from '../components/parties/ContactFieldsForm';

const EMPTY_CONTACT_FIELDS: ContactFieldsFormValue = {
  firstName: '', middleName: '', lastName: '', phone: '', email: '', street: '', city: '', state: '', zip: '', country: '',
};

export interface UsePartyFormOptions {
  proofGraph: ProofGraph | null;
  saveGraph: (graph: ProofGraph) => void | Promise<void>;
  setContacts: (updater: (prev: ClioContact[]) => ClioContact[]) => void;
  showConfirm: (title: string, message: string, onConfirm: () => void) => void;
  responsibleAttorney?: ClioContact | null | undefined;
}

// Owns the full add/edit party form: the party's own fields, the "create a
// new primary contact" sub-form, the "create a new counsel contact"
// sub-form, and the related-contacts sub-form — resolving two duplication
// hotspots that used to live in App.tsx: openAddPartyModal/openEditPartyModal
// each resetting ~25 fields by hand (now a single loadForm(party?)), and
// handleSaveParty's two near-identical "create contact via POST" blocks (now
// both funnel through shared/lib/contacts.ts's createContact()).
export function usePartyForm({ proofGraph, saveGraph, setContacts, showConfirm, responsibleAttorney }: UsePartyFormOptions): UsePartyFormResult {
  const [isOpen, setIsOpen] = useState(false);
  const [editingParty, setEditingParty] = useState<Party | null>(null);

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [type, setTypeState] = useState<'us' | 'them' | 'tp'>('us');

  const [contactId, setContactId] = useState('');
  const [createNewContact, setCreateNewContact] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactType, setNewContactType] = useState('Individual');
  const [newContactFields, setNewContactFieldsState] = useState<ContactFieldsFormValue>(EMPTY_CONTACT_FIELDS);
  const setNewContactFields = (patch: Partial<ContactFieldsFormValue>): void => setNewContactFieldsState(prev => ({ ...prev, ...patch }));

  const [counselIds, setCounselIds] = useState<string[]>([]);
  const [createNewCounsel, setCreateNewCounsel] = useState(false);
  const [newCounselFields, setNewCounselFieldsState] = useState<ContactFieldsFormValue>(EMPTY_CONTACT_FIELDS);
  const setNewCounselFields = (patch: Partial<ContactFieldsFormValue>): void => setNewCounselFieldsState(prev => ({ ...prev, ...patch }));
  const [newCounselCreateFirm, setNewCounselCreateFirm] = useState(false);
  const [newCounselFirmId, setNewCounselFirmId] = useState('');
  const [newCounselFirmName, setNewCounselFirmName] = useState('');
  const [paralegalIds, setParalegalIds] = useState<string[]>([]);

  const addCounselId = (contactId: string): void => {
    if (!contactId) return;
    setCounselIds(prev => prev.includes(contactId) ? prev : [...prev, contactId]);
  };

  const removeCounselId = (contactId: string): void => {
    setCounselIds(prev => prev.filter(id => id !== contactId));
  };

  const addParalegalId = (contactId: string): void => {
    if (!contactId) return;
    setParalegalIds(prev => prev.includes(contactId) ? prev : [...prev, contactId]);
  };

  const removeParalegalId = (contactId: string): void => {
    setParalegalIds(prev => prev.filter(id => id !== contactId));
  };

  const setType = (nextType: 'us' | 'them' | 'tp'): void => {
    setTypeState(nextType);
    if (nextType === 'us' && responsibleAttorney?.id) {
      setCreateNewCounsel(false);
      setCounselIds([responsibleAttorney.id]);
    } else if (responsibleAttorney?.id) {
      setCounselIds(prev => prev.filter(id => id !== responsibleAttorney.id));
    }
  };

  const [relatedContacts, setRelatedContacts] = useState<RelatedContact[]>([]);
  const [isAddingRelated, setIsAddingRelated] = useState(false);
  const [relatedFormContactId, setRelatedFormContactId] = useState('');
  const [relatedFormLabel, setRelatedFormLabel] = useState('');
  const [relatedFormIsDepo, setRelatedFormIsDepo] = useState(false);
  const [relatedFormCreateNew, setRelatedFormCreateNew] = useState(false);
  const [relatedFormNewName, setRelatedFormNewName] = useState('');

  const resetForm = (): void => {
    setContactId('');
    setCreateNewContact(false);
    setNewContactName('');
    setNewContactType('Individual');
    setNewContactFieldsState(EMPTY_CONTACT_FIELDS);
    setCounselIds([]);
    setCreateNewCounsel(false);
    setNewCounselFieldsState(EMPTY_CONTACT_FIELDS);
    setNewCounselCreateFirm(false);
    setNewCounselFirmId('');
    setNewCounselFirmName('');
    setParalegalIds([]);
    setIsAddingRelated(false);
  };

  const openAdd = (): void => {
    setEditingParty(null);
    setName('');
    setRole('Plaintiff');
    resetForm();
    setTypeState('us');
    if (responsibleAttorney?.id) {
      setCounselIds([responsibleAttorney.id]);
    }
    setRelatedContacts([]);
    setIsOpen(true);
  };

  const openEdit = (party: Party): void => {
    setEditingParty(party);
    setName(party.name);
    setRole(party.role);
    const VALID_PARTY_TYPES = new Set<'us' | 'them' | 'tp'>(['us', 'them', 'tp']);
    setTypeState(VALID_PARTY_TYPES.has(party.type) ? party.type : 'tp');
    resetForm();
    setContactId((party.clio_contact_id ?? ''));
    setCounselIds(getPartyCounselContactIds(party));
    setParalegalIds(getPartyParalegalContactIds(party));
    setRelatedContacts(party.related_contacts ? [...party.related_contacts] : []);
    setIsOpen(true);
  };

  const close = (): void => setIsOpen(false);

  const addRelatedContact = async (contactsList: ClioContact[]): Promise<void> => {
    let relContactId = relatedFormContactId;
    let contactName = (contactsList.find(c => c.id === relContactId)?.name ?? '');
    if (relatedFormCreateNew && relatedFormNewName.trim()) {
      const created = await createContact({ name: relatedFormNewName.trim(), type: 'Individual' });
      if (!created) return;
      setContacts(prev => [...prev, created]);
      relContactId = created.id;
      contactName = created.name;
    }
    if (!relContactId || !relatedFormLabel.trim()) return;
    const newRelated: RelatedContact = {
      id: `rc_${Date.now()}`,
      clio_contact_id: relContactId,
      name: contactName,
      label: relatedFormLabel.trim(),
      is_deposition_individual: relatedFormIsDepo,
    };
    setRelatedContacts(prev => [...prev, newRelated]);
    setRelatedFormContactId('');
    setRelatedFormLabel('');
    setRelatedFormIsDepo(false);
    setRelatedFormCreateNew(false);
    setRelatedFormNewName('');
    setIsAddingRelated(false);
  };

  const removeRelatedContact = (index: number): void => {
    setRelatedContacts(prev => prev.filter((_, i) => i !== index));
  };

  const save = async (e: Pick<React.FormEvent, 'preventDefault'>): Promise<void> => {
    e.preventDefault();
    if (!proofGraph) return;
    let finalContactId = contactId;
    const finalCounselIds = [...counselIds];

    if (createNewContact) {
      let finalContactName = newContactName.trim();
      if (newContactType === 'Individual') {
        finalContactName = [newContactFields.firstName, newContactFields.middleName, newContactFields.lastName]
          .map(s => s.trim()).filter(Boolean).join(' ');
      }
      if (finalContactName) {
        const payload: CreateContactPayload = {
          name: finalContactName,
          type: newContactType,
          first_name: newContactType === 'Individual' ? newContactFields.firstName.trim() : '',
          middle_name: newContactType === 'Individual' ? newContactFields.middleName.trim() : '',
          last_name: newContactType === 'Individual' ? newContactFields.lastName.trim() : '',
          phone: newContactFields.phone.trim(),
          email: newContactFields.email.trim(),
          street: newContactFields.street.trim(),
          city: newContactFields.city.trim(),
          state: newContactFields.state.trim(),
          zip: newContactFields.zip.trim(),
          country: newContactFields.country.trim(),
        };
        const created = await createContact(payload);
        if (created) {
          setContacts(prev => [...prev, created]);
          finalContactId = created.id;
        }
      }
    }

    if (createNewCounsel) {
      const finalCounselName = [newCounselFields.firstName, newCounselFields.middleName, newCounselFields.lastName]
        .map(s => s.trim()).filter(Boolean).join(' ');
      if (finalCounselName) {
        let companyIdToLink = '';
        if (newCounselCreateFirm && newCounselFirmName.trim()) {
          const createdFirm = await createContact({ name: newCounselFirmName.trim(), type: 'Company' });
          if (createdFirm) {
            setContacts(prev => [...prev, createdFirm]);
            companyIdToLink = createdFirm.id;
          }
        } else if (newCounselFirmId) {
          companyIdToLink = newCounselFirmId;
        }

        const created = await createContact({
          name: finalCounselName,
          type: 'Individual',
          first_name: newCounselFields.firstName.trim(),
          middle_name: newCounselFields.middleName.trim(),
          last_name: newCounselFields.lastName.trim(),
          phone: newCounselFields.phone.trim(),
          email: newCounselFields.email.trim(),
          street: newCounselFields.street.trim(),
          city: newCounselFields.city.trim(),
          state: newCounselFields.state.trim(),
          zip: newCounselFields.zip.trim(),
          country: newCounselFields.country.trim(),
          company_id: companyIdToLink,
        });
        if (created) {
          setContacts(prev => [...prev, created]);
          if (!finalCounselIds.includes(created.id)) finalCounselIds.push(created.id);
        }
      }
    }

    const updatedParty = {
      id: (nonEmptyString(editingParty?.id) ?? `p_${Date.now()}`),
      name: name.trim(),
      role: role.trim(),
      type,
      ...(finalContactId ? { clio_contact_id: finalContactId } : {}),
      ...counselFields(finalCounselIds),
      paralegal_contact_ids: paralegalIds,
      related_contacts: relatedContacts,
    } as Party;

    const currentParties: Party[] = proofGraph.parties ?? [];
    const newParties = editingParty
      ? currentParties.map((p: Party) => p.id === editingParty.id ? updatedParty : p)
      : [...currentParties, updatedParty];

    await saveGraph({ ...proofGraph, parties: newParties });
    setIsOpen(false);
  };

  const deleteParty = (partyId: string): void => {
    showConfirm('Remove Party', 'Are you sure you want to remove this party from the matter?', () => {
      if (!proofGraph) return;
      const newParties = (proofGraph.parties ?? []).filter((p: Party) => p.id !== partyId);
      void saveGraph({ ...proofGraph, parties: newParties });
    });
  };

  return {
    isOpen, editingParty, close,
    name, setName, role, setRole, type, setType,
    contactId, setContactId, createNewContact, setCreateNewContact,
    newContactName, setNewContactName, newContactType, setNewContactType,
    newContactFields, setNewContactFields,
    counselIds, addCounselId, removeCounselId, createNewCounsel, setCreateNewCounsel,
    newCounselFields, setNewCounselFields,
    newCounselCreateFirm, setNewCounselCreateFirm, newCounselFirmId, setNewCounselFirmId, newCounselFirmName, setNewCounselFirmName,
    paralegalIds, addParalegalId, removeParalegalId,
    relatedContacts, removeRelatedContact,
    isAddingRelated, setIsAddingRelated,
    relatedFormContactId, setRelatedFormContactId,
    relatedFormLabel, setRelatedFormLabel,
    relatedFormIsDepo, setRelatedFormIsDepo,
    relatedFormCreateNew, setRelatedFormCreateNew,
    relatedFormNewName, setRelatedFormNewName,
    addRelatedContact,
    openAdd, openEdit,
    save, deleteParty,
  };
}

export interface UsePartyFormResult {
    isOpen: boolean;
    editingParty: Party | null;
    close: () => void;
    name: string;
    setName: import("react").Dispatch<import("react").SetStateAction<string>>;
    role: string;
    setRole: import("react").Dispatch<import("react").SetStateAction<string>>;
    type: "us" | "them" | "tp";
    setType: (nextType: "us" | "them" | "tp") => void;
    contactId: string;
    setContactId: import("react").Dispatch<import("react").SetStateAction<string>>;
    createNewContact: boolean;
    setCreateNewContact: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    newContactName: string;
    setNewContactName: import("react").Dispatch<import("react").SetStateAction<string>>;
    newContactType: string;
    setNewContactType: import("react").Dispatch<import("react").SetStateAction<string>>;
    newContactFields: ContactFieldsFormValue;
    setNewContactFields: (patch: Partial<ContactFieldsFormValue>) => void;
    counselIds: string[];
    addCounselId: (contactId: string) => void;
    removeCounselId: (contactId: string) => void;
    createNewCounsel: boolean;
    setCreateNewCounsel: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    newCounselFields: ContactFieldsFormValue;
    setNewCounselFields: (patch: Partial<ContactFieldsFormValue>) => void;
    newCounselCreateFirm: boolean;
    setNewCounselCreateFirm: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    newCounselFirmId: string;
    setNewCounselFirmId: import("react").Dispatch<import("react").SetStateAction<string>>;
    newCounselFirmName: string;
    setNewCounselFirmName: import("react").Dispatch<import("react").SetStateAction<string>>;
    paralegalIds: string[];
    addParalegalId: (contactId: string) => void;
    removeParalegalId: (contactId: string) => void;
    relatedContacts: RelatedContact[];
    removeRelatedContact: (index: number) => void;
    isAddingRelated: boolean;
    setIsAddingRelated: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    relatedFormContactId: string;
    setRelatedFormContactId: import("react").Dispatch<import("react").SetStateAction<string>>;
    relatedFormLabel: string;
    setRelatedFormLabel: import("react").Dispatch<import("react").SetStateAction<string>>;
    relatedFormIsDepo: boolean;
    setRelatedFormIsDepo: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    relatedFormCreateNew: boolean;
    setRelatedFormCreateNew: import("react").Dispatch<import("react").SetStateAction<boolean>>;
    relatedFormNewName: string;
    setRelatedFormNewName: import("react").Dispatch<import("react").SetStateAction<string>>;
    addRelatedContact: (contactsList: ClioContact[]) => Promise<void>;
    openAdd: () => void;
    openEdit: (party: Party) => void;
    save: (e: Pick<React.FormEvent, "preventDefault">) => Promise<void>;
    deleteParty: (partyId: string) => void;
}

